import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  TextField,
} from '@mui/material';
import { ChevronLeft, MousePointer2, ListFilter, ArrowRight } from 'lucide-react';
import {
  DndContext,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import styles from './CategoryGames.module.scss';
import { GameCategory } from './CategoryItem';
import GameCard, { Game } from './GameCard';
import ConfirmDeleteModal from 'components/modals/ConfirmDeleteModal';
import { gameCategoryService } from 'components/api/gameCategories';
import { gamesService } from 'components/api/games';

interface SubProvider {
  id: number;
  name: string;
  displayName?: string;
  providerId: number;
  providerCode: string;
  isEnabled: boolean;
  image: string | null;
  categories?: string[];
}

interface CategoryGamesProps {
  category: GameCategory;
  onBack: () => void;
}

const DroppableZone: React.FC<{ id: string; children: React.ReactNode; className?: string }> = ({
  id,
  children,
  className,
}) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={className} id={id}>
      {children}
    </div>
  );
};

const CategoryGames: React.FC<CategoryGamesProps> = ({ category, onBack }) => {
  const dispatch = useAppDispatch();
  const { id: siteId } = useParams<{ id: string }>();
  const [providers, setProviders] = useState<SubProvider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<number | string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isLiveOnly, setIsLiveOnly] = useState<boolean>(false);

  const [availableGames, setAvailableGames] = useState<Game[]>([]);
  const [gamesPage, setGamesPage] = useState<number>(1);
  const [gamesTotal, setGamesTotal] = useState<number>(0);
  const [isGamesLoading, setIsGamesLoading] = useState<boolean>(false);

  const [categoryGames, setCategoryGames] = useState<Game[]>([]);
  const [catGamesPage, setCatGamesPage] = useState<number>(1);
  const [catGamesTotal, setCatGamesTotal] = useState<number>(0);
  const [isCatGamesLoading, setIsCatGamesLoading] = useState<boolean>(false);

  const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: '', title: '' });
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [newDraggedGameIds, setNewDraggedGameIds] = useState<string[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [catSearchValue, setCatSearchValue] = useState('');
  const [isCatSearching, setIsCatSearching] = useState(false);
  const [catSearchGames, setCatSearchGames] = useState<Game[]>([]);
  const [catSearchPage, setCatSearchPage] = useState<number>(1);
  const [catSearchTotal, setCatSearchTotal] = useState<number>(0);

  const activeProvider = useMemo(() => {
    return providers.find((p) => p.id === Number(selectedProviderId));
  }, [providers, selectedProviderId]);

  const availableCategories = useMemo(() => {
    return activeProvider?.categories || [];
  }, [activeProvider]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const fetchAvailableGames = async (
    provId: number | string,
    page: number,
    append = false,
    catFilter = selectedCategory,
    liveFilter = isLiveOnly,
    searchVal = searchValue,
  ) => {
    if (!provId) return;
    try {
      setIsGamesLoading(true);
      const res = await gameCategoryService.getGamesBySubProvider({
        subProviderId: provId,
        page,
        pageSize: 16,
        siteId,
        category: isSearchMode ? undefined : catFilter === '' ? undefined : catFilter,
        isLive: isSearchMode ? undefined : liveFilter ? true : undefined,
        categoryId: category.id,
        search: isSearchMode && searchVal ? searchVal : undefined,
      });
      const items = res?.data?.items || [];
      const total = res?.data?.total || 0;

      const formatted = items.map((game: any) => ({
        id: String(game.id),
        title: game.title,
        provider: game.producer,
        image: game.imageUrl,
        inCategory: game.inCategory,
      }));

      if (append) {
        setAvailableGames((prev) => {
          const filteredPrev = prev.filter(
            (existing) => !formatted.find((f: any) => f.id === existing.id),
          );
          return [...filteredPrev, ...formatted];
        });
      } else {
        setAvailableGames(formatted);
      }
      setGamesTotal(total);
    } catch (err) {
      console.error('Failed to load games', err);
    } finally {
      setIsGamesLoading(false);
    }
  };

  const fetchCategoryGames = async (catId: number | string, page: number, append = false) => {
    if (!catId) return;
    try {
      setIsCatGamesLoading(true);
      const res = await gameCategoryService.getGamesByCategory({
        categoryId: catId,
        page,
        pageSize: 16,
        siteId,
      });
      const items = res?.data?.items || [];
      const total = res?.data?.total || 0;

      const formatted = items.map((game: any) => ({
        id: String(game.id),
        title: game.title,
        provider: game.producer,
        image: game.imageUrl,
        isEnabled: game.isEnabled,
      }));

      if (append) {
        setCategoryGames((prev) => {
          const filteredPrev = prev.filter(
            (existing) => !formatted.find((f: any) => f.id === existing.id),
          );
          return [...filteredPrev, ...formatted];
        });
      } else {
        setCategoryGames(formatted);
      }
      setCatGamesTotal(total);
    } catch (err) {
      console.error('Failed to load category games', err);
    } finally {
      setIsCatGamesLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (catSearchValue.trim()) {
        fetchCatSearchGames(1, false);
      } else {
        setCatSearchGames([]);
        setCatSearchPage(1);
        setCatSearchTotal(0);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [catSearchValue, category.id, siteId]);

  const fetchCatSearchGames = async (page: number, append = false) => {
    try {
      setIsCatSearching(true);
      const res = await gameCategoryService.getGamesByCategory({
        categoryId: category.id,
        search: catSearchValue.trim(),
        page,
        pageSize: 16,
        siteId,
      });
      const items = res?.data?.items || [];
      const total = res?.data?.total || 0;

      const formatted = items.map((game: any) => ({
        id: String(game.id),
        title: game.title,
        provider: game.producer,
        image: game.imageUrl,
        isEnabled: game.isEnabled,
      }));

      if (append) {
        setCatSearchGames((prev) => {
          const filteredPrev = prev.filter(
            (existing) => !formatted.find((f: any) => f.id === existing.id),
          );
          return [...filteredPrev, ...formatted];
        });
      } else {
        setCatSearchGames(formatted);
      }
      setCatSearchTotal(total);
      setCatSearchPage(page);
    } catch (err) {
      console.error('Failed to search category games', err);
    } finally {
      setIsCatSearching(false);
    }
  };

  const handleLoadMoreCatSearch = () => {
    if (catSearchGames.length < catSearchTotal) {
      fetchCatSearchGames(catSearchPage + 1, true);
    }
  };

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await gameCategoryService.getSubProviders();
        const list = response?.data || [];
        setProviders(list);
        if (list.length > 0) {
          const firstId = list[0].id;
          setSelectedProviderId(firstId);
          fetchAvailableGames(firstId, 1, false);
        }
      } catch (err) {
        console.error('Failed to load sub-providers', err);
      }
    };
    fetchProviders();
    fetchCategoryGames(category.id, 1, false);
    setNewDraggedGameIds([]);
  }, [category.id]);

  useEffect(() => {
    if (!isSearchMode) return;

    const handler = setTimeout(() => {
      setGamesPage(1);
      setSelectedGameIds(new Set());
      setIsAllSelected(false);
      fetchAvailableGames(selectedProviderId, 1, false, '', false, searchValue);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue, isSearchMode, selectedProviderId]);

  const availableGamesWithDisabled = useMemo(() => {
    return availableGames.map((game) => ({
      ...game,
      dndId: `avail-${game.id}`,
      isDisabled: game.inCategory || !!categoryGames.find((cg) => cg.id === game.id),
    }));
  }, [availableGames, categoryGames]);

  const activeAvailableGames = useMemo(() => {
    return availableGamesWithDisabled.filter((g) => !g.isDisabled);
  }, [availableGamesWithDisabled]);

  const handleSelectAllToggle = (checked: boolean) => {
    setIsAllSelected(checked);
    const newSelected = new Set(selectedGameIds);
    if (checked) {
      activeAvailableGames.forEach((g) => {
        newSelected.add(g.id);
      });
    } else {
      activeAvailableGames.forEach((g) => {
        newSelected.delete(g.id);
      });
    }
    setSelectedGameIds(newSelected);
  };

  const handleProviderChange = (providerId: string | number) => {
    setSelectedProviderId(providerId);
    setSelectedCategory('');
    setIsLiveOnly(false);
    setGamesPage(1);
    setSelectedGameIds(new Set());
    setIsAllSelected(false);
    if (isSearchMode) {
      setSearchValue('');
    } else {
      fetchAvailableGames(providerId, 1, false, '', false);
    }
  };

  const handleCategoryChange = (categoryName: string) => {
    setSelectedCategory(categoryName);
    setGamesPage(1);
    setSelectedGameIds(new Set());
    setIsAllSelected(false);
    fetchAvailableGames(selectedProviderId, 1, false, categoryName, isLiveOnly);
  };

  const handleLiveOnlyToggle = (checked: boolean) => {
    setIsLiveOnly(checked);
    setGamesPage(1);
    setSelectedGameIds(new Set());
    setIsAllSelected(false);
    fetchAvailableGames(selectedProviderId, 1, false, selectedCategory, checked);
  };

  const handleLoadMoreGames = () => {
    const nextPage = gamesPage + 1;
    setGamesPage(nextPage);
    fetchAvailableGames(selectedProviderId, nextPage, true, selectedCategory, isLiveOnly);
  };

  const handleLoadMoreCategoryGames = () => {
    const nextPage = catGamesPage + 1;
    setCatGamesPage(nextPage);
    fetchCategoryGames(category.id, nextPage, true);
  };

  const handleSelectGame = (id: string) => {
    const newSelected = new Set(selectedGameIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
      setIsAllSelected(false);
    } else {
      newSelected.add(id);
      const allSelectedAfterAdd = activeAvailableGames.every(
        (g) => g.id === id || newSelected.has(g.id),
      );
      if (allSelectedAfterAdd) {
        setIsAllSelected(true);
      }
    }
    setSelectedGameIds(newSelected);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeDndId = active.id as string;
    const overDndId = over.id as string;

    // Dragging from Available (Left Panel) to Category (Right Panel)
    const isOverRightPanel = overDndId === 'right-panel' || overDndId.startsWith('cat-');

    if (isOverRightPanel && activeDndId.startsWith('avail-')) {
      const activeGameId = activeDndId.replace('avail-', '');
      const activeGame = availableGames.find((g) => g.id === activeGameId);
      if (!activeGame) return;

      const gamesToAdd = selectedGameIds.has(activeGameId)
        ? availableGames.filter((g) => selectedGameIds.has(g.id))
        : [activeGame];

      const newGames = gamesToAdd
        .filter((g) => !categoryGames.find((cg) => cg.id === g.id))
        .map((g) => ({ ...g, isEnabled: true }));

      if (newGames.length > 0) {
        setCategoryGames((prev) => [...newGames, ...prev]);
        setCatGamesTotal((prev) => prev + newGames.length);
        setNewDraggedGameIds((prev) => [...prev, ...newGames.map((g) => g.id)]);
        setSelectedGameIds(new Set());
        setHasChanges(true);
      }
    }
  };

  const handleSaveOrder = async () => {
    try {
      setIsSaving(true);
      if (isAllSelected) {
        const updateData: any = {
          subProviderId: selectedProviderId,
        };
        if (isLiveOnly) {
          updateData.isLive = isLiveOnly;
        }
        if (selectedCategory) {
          updateData.gameCategory = selectedCategory;
        }
        await gameCategoryService.updateCategory(category.id, updateData);
      } else {
        const gameIds = newDraggedGameIds.map((id) => {
          const parsed = Number(id);
          return isNaN(parsed) ? id : parsed;
        });
        await gameCategoryService.updateCategory(category.id, {
          gameIds,
        });
      }
      setHasChanges(false);
      setNewDraggedGameIds([]);
      dispatch(showToast({ text: 'Category games updated successfully!', type: 'success' }));
    } catch (error) {
      console.error('Failed to update category games:', error);
      dispatch(showToast({ text: 'Failed to update category games', type: 'error' }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveGame = (id: string) => {
    const game = categoryGames.find((g) => g.id === id) || catSearchGames.find((g) => g.id === id);
    if (game) {
      setDeleteModal({ open: true, id, title: game.title });
    }
  };

  const handleToggleGameEnable = async (gameId: string, isEnabled: boolean) => {
    // Optimistic update
    setCategoryGames((prev) => prev.map((g) => (g.id === gameId ? { ...g, isEnabled } : g)));
    setCatSearchGames((prev) => prev.map((g) => (g.id === gameId ? { ...g, isEnabled } : g)));
    try {
      await gamesService.toggleGame(gameId, isEnabled);
      dispatch(showToast({ text: 'Game status updated successfully', type: 'success' }));
    } catch (error) {
      // Revert optimistic update
      setCategoryGames((prev) =>
        prev.map((g) => (g.id === gameId ? { ...g, isEnabled: !isEnabled } : g)),
      );
      setCatSearchGames((prev) =>
        prev.map((g) => (g.id === gameId ? { ...g, isEnabled: !isEnabled } : g)),
      );
      console.error('Failed to toggle game status:', error);
      dispatch(showToast({ text: 'Failed to update game status', type: 'error' }));
    }
  };

  const confirmRemoveGame = async () => {
    const gameId = deleteModal.id;
    const isNewGame = newDraggedGameIds.includes(gameId);

    setDeleteModal((prev) => ({ ...prev, open: false }));

    try {
      if (!isNewGame) {
        await gameCategoryService.updateCategory(category.id, {
          siteId: Number(siteId),
          removeGameIds: [Number(gameId)],
        });
      }

      setCategoryGames((prev) => prev.filter((g) => g.id !== gameId));
      setCatSearchGames((prev) => prev.filter((g) => g.id !== gameId));
      setAvailableGames((prev) =>
        prev.map((g) => (g.id === gameId ? { ...g, inCategory: false } : g)),
      );
      setCatGamesTotal((prev) => Math.max(0, prev - 1));
      if (catSearchValue.trim()) {
        setCatSearchTotal((prev) => Math.max(0, prev - 1));
      }

      setNewDraggedGameIds((prev) => {
        const updated = prev.filter((id) => id !== gameId);
        // If we removed the last unsaved dragged game, and we have no other unsaved changes, remove the save button
        if (updated.length === 0 && !isAllSelected) {
          setHasChanges(false);
        }
        return updated;
      });

      if (!isNewGame) {
        dispatch(showToast({ text: 'Game removed from category successfully', type: 'success' }));
      }
    } catch (error) {
      console.error('Failed to remove game:', error);
      dispatch(showToast({ text: 'Failed to remove game from category', type: 'error' }));
    }
  };

  const activeGame = useMemo(() => {
    if (!activeId) return null;
    const actualId = activeId.replace('avail-', '').replace('cat-', '');
    return (
      categoryGames.find((g) => g.id === actualId) ||
      availableGames.find((g) => g.id === actualId) ||
      null
    );
  }, [activeId, categoryGames, availableGames]);

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box className={styles.backBtn} onClick={onBack}>
          <ChevronLeft size={28} />
        </Box>
        <Typography variant="h5" className={styles.title}>
          {category.title}
        </Typography>
        {hasChanges && (
          <Button
            variant="contained"
            className={styles.saveOrderBtn}
            onClick={handleSaveOrder}
            disabled={isSaving}
            sx={{ ml: 'auto' }}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Box className={styles.mainLayout}>
          {/* Left Panel: Available Games */}
          <Box className={styles.panel}>
            <Box className={styles.panelHeader}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  mb: 1,
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ListFilter size={18} color="#8698a6" />
                    <Typography variant="h6" className={styles.panelTitle}>
                      Available Games
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant={isSearchMode ? 'contained' : 'outlined'}
                    onClick={() => {
                      const nextMode = !isSearchMode;
                      setIsSearchMode(nextMode);
                      if (!nextMode) {
                        setSearchValue('');
                        fetchAvailableGames(
                          selectedProviderId,
                          1,
                          false,
                          selectedCategory,
                          isLiveOnly,
                          '',
                        );
                      }
                      setGamesPage(1);
                      setSelectedGameIds(new Set());
                      setIsAllSelected(false);
                    }}
                    sx={{
                      fontSize: '11px',
                      py: 0.1,
                      px: 1.2,
                      minWidth: 'auto',
                      height: '24px',
                      textTransform: 'none',
                      borderColor: '#4d6273',
                      backgroundColor: isSearchMode ? 'var(--accent-blue)' : 'transparent',
                      color: '#ffffff',
                      borderRadius: '6px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#ffffff',
                        backgroundColor: isSearchMode
                          ? 'var(--accent-blue-hover)'
                          : 'rgba(255,255,255,0.05)',
                      },
                    }}
                  >
                    {isSearchMode ? 'Filters' : 'Search Game'}
                  </Button>
                </Box>
                {!isSearchMode && (
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={isAllSelected}
                        onChange={(e) => handleSelectAllToggle(e.target.checked)}
                        sx={{
                          '&.Mui-checked': { color: 'var(--accent-blue)' },
                          '&.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: 'var(--accent-blue)',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ color: '#8698a6' }}>
                        Select All
                      </Typography>
                    }
                    sx={{ mr: 0 }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, width: '100%', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                  <FormControl fullWidth size="small">
                    <InputLabel
                      sx={{
                        color: '#8698a6',
                        '&.Mui-focused': {
                          color: '#ffffff',
                        },
                      }}
                    >
                      Provider
                    </InputLabel>
                    <Select
                      value={selectedProviderId}
                      label="Provider"
                      onChange={(e) => handleProviderChange(e.target.value)}
                      className={styles.providerSelect}
                    >
                      {providers.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.displayName || p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {isSearchMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search game..."
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      className={styles.providerSelect}
                      sx={{
                        input: {
                          color: '#ffffff',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: '#8698a6',
                          opacity: 1,
                        },
                      }}
                    />
                  ) : (
                    <FormControl fullWidth size="small">
                      <InputLabel
                        sx={{
                          color: '#8698a6',
                          '&.Mui-focused': {
                            color: '#ffffff',
                          },
                        }}
                      >
                        Category
                      </InputLabel>
                      <Select
                        value={selectedCategory}
                        label="Category"
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        className={styles.providerSelect}
                      >
                        <MenuItem value="">All Categories</MenuItem>
                        {availableCategories.map((cat) => (
                          <MenuItem key={cat} value={cat}>
                            {cat
                              .split('_')
                              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                              .join(' ')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
                {!isSearchMode && (
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={isLiveOnly}
                        onChange={(e) => handleLiveOnlyToggle(e.target.checked)}
                        sx={{
                          '&.Mui-checked': { color: 'var(--accent-blue)' },
                          '&.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: 'var(--accent-blue)',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ color: '#8698a6' }}>
                        Live Games Only
                      </Typography>
                    }
                    sx={{ mt: -0.5, ml: 0.5 }}
                  />
                )}
              </Box>
            </Box>
            <Box className={styles.panelContent}>
              {availableGamesWithDisabled.map((game) => (
                <GameCard
                  key={game.dndId}
                  game={game}
                  dndId={game.dndId}
                  isSelected={selectedGameIds.has(game.id)}
                  onSelect={handleSelectGame}
                  isMultiSelectMode={true}
                  isSortable={!game.isDisabled}
                  isDisabled={game.isDisabled}
                />
              ))}

              {isGamesLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} sx={{ color: 'var(--accent-blue)' }} />
                </Box>
              )}

              {availableGames.length < gamesTotal && !isGamesLoading && (
                <Button
                  className={styles.loadMoreBtn}
                  onClick={handleLoadMoreGames}
                  disabled={isAllSelected}
                >
                  Load More ({gamesTotal - availableGames.length} left)
                </Button>
              )}
              {availableGames.length === 0 && !isGamesLoading && (
                <Box className={styles.emptyState}>
                  <MousePointer2 size={40} opacity={0.3} />
                  <Typography variant="body2">No more games for this provider.</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Right Panel: Category Games */}
          <DroppableZone id="right-panel" className={styles.panel}>
            <Box className={styles.panelHeader}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', gap: 1 }}>
                  <ArrowRight size={18} color="var(--accent-blue)" />
                  <Typography variant="h6" className={styles.panelTitle}>
                    Games in {category.title}
                  </Typography>
                </Box>
                <TextField
                  placeholder="Search category games..."
                  value={catSearchValue}
                  onChange={(e) => setCatSearchValue(e.target.value)}
                  size="small"
                  className={styles.providerSelect}
                  sx={{
                    width: 250,
                    input: {
                      color: '#ffffff',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#8698a6',
                      opacity: 1,
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: '#8698a6' }}>
                {catSearchValue.trim()
                  ? `${catSearchGames.length} of ${catSearchTotal}`
                  : `${categoryGames.length} of ${catGamesTotal}`}{' '}
                games.
              </Typography>
            </Box>
            <Box className={styles.panelContent}>
              {(catSearchValue.trim() ? catSearchGames : categoryGames).map((game) => (
                <GameCard
                  key={game.id}
                  game={game}
                  dndId={`cat-${game.id}`}
                  isSortable={!catSearchValue.trim()}
                  onRemove={handleRemoveGame}
                  showEnableToggle={true}
                  onToggleEnable={handleToggleGameEnable}
                />
              ))}

              {(isCatGamesLoading || isCatSearching) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} sx={{ color: 'var(--accent-blue)' }} />
                </Box>
              )}

              {catSearchValue.trim() &&
                catSearchGames.length < catSearchTotal &&
                !isCatSearching && (
                  <Button
                    onClick={handleLoadMoreCatSearch}
                    variant="outlined"
                    sx={{
                      mt: 2,
                      mb: 2,
                      color: 'var(--accent-blue)',
                      borderColor: 'var(--accent-blue)',
                    }}
                  >
                    Load More
                  </Button>
                )}

              {!catSearchValue.trim() &&
                categoryGames.length < catGamesTotal &&
                !isCatGamesLoading && (
                  <Button className={styles.loadMoreBtn} onClick={handleLoadMoreCategoryGames}>
                    Load More ({catGamesTotal - categoryGames.length} left)
                  </Button>
                )}

              {!catSearchValue.trim() && categoryGames.length === 0 && !isCatGamesLoading && (
                <Box className={styles.emptyState}>
                  <Box sx={{ mb: 1 }}>🎯</Box>
                  <Typography variant="body2">Drag games here to add to category.</Typography>
                </Box>
              )}
            </Box>
          </DroppableZone>
        </Box>

        <DragOverlay dropAnimation={null}>
          {activeGame ? (
            <Box sx={{ pointerEvents: 'none' }}>
              <GameCard
                game={activeGame}
                dndId={activeId as string}
                isSortable={true}
                isSelected={selectedGameIds.has(activeGame.id)}
                isMultiSelectMode={selectedGameIds.size > 1}
              />
              {selectedGameIds.size > 1 && selectedGameIds.has(activeGame.id) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    background: 'var(--accent-blue)',
                    color: 'white',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    zIndex: 1001,
                  }}
                >
                  {selectedGameIds.size}
                </Box>
              )}
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      <ConfirmDeleteModal
        open={deleteModal.open}
        title={deleteModal.title}
        onClose={() => setDeleteModal({ ...deleteModal, open: false })}
        onConfirm={confirmRemoveGame}
      />
    </Box>
  );
};

export default CategoryGames;
